class RedPromoBar extends HTMLElement {
  constructor() {
    super()
    this.currentIndex = 0
    this.timer = null
    this.isTransitioning = false
  }

  connectedCallback() {
    // Wait for the browser to finish parsing the children and attributes
    requestAnimationFrame(() => {
      this.init()
    })

    // Theme Editor support
    this._onBlockSelect = this.handleBlockSelect.bind(this)
    this._onBlockDeselect = this.handleBlockDeselect.bind(this)
    this.addEventListener("shopify:block:select", this._onBlockSelect)
    this.addEventListener("shopify:block:deselect", this._onBlockDeselect)
  }

  init() {
    this.slider = this.querySelector(".promo-bar__slider")
    this.slides = Array.from(this.querySelectorAll(".promo-bar__slide"))
    if (this.slides.length === 0) return

    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed) || 5000
    this.animationSpeed = parseFloat(this.dataset.animationSpeed) || 0.6
    this.direction = this.dataset.direction || "right-to-left"

    // If there is only one slide, clone it to make a seamless infinite loop animation possible
    if (this.slides.length === 1) {
      const clone = this.slides[0].cloneNode(true)
      clone.classList.remove("is-active")
      clone.setAttribute("data-index", "1")
      this.slider.appendChild(clone)
      this.slides.push(clone)
    }

    // Set transition duration custom property dynamically based on settings
    this.style.setProperty("--transition-duration", `${this.animationSpeed}s`)

    // Reset slide states to entering
    this.slides.forEach(slide => {
      slide.classList.remove("is-active", "is-exiting")
      slide.classList.add("is-entering")
    })

    // Force layout reflow before triggering load-time transition
    this.offsetHeight

    // Trigger the slide-in animation for the active slide immediately on load
    const activeSlide = this.slides[this.currentIndex]
    activeSlide.classList.remove("is-entering")
    activeSlide.classList.add("is-active")

    this.startAutoplay()

    // Pause on hover
    this._onMouseEnter = () => this.stopAutoplay()
    this._onMouseLeave = () => this.startAutoplay()
    this.addEventListener("mouseenter", this._onMouseEnter)
    this.addEventListener("mouseleave", this._onMouseLeave)
  }

  startAutoplay() {
    this.stopAutoplay()
    // Only autoplay if we actually have slides to animate
    if (this.slides.length > 1) {
      this.timer = setInterval(() => {
        this.next()
      }, this.autoplaySpeed)
    }
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  next() {
    if (this.isTransitioning) return
    this.goTo(this.currentIndex + 1)
  }

  goTo(nextIndex) {
    if (this.isTransitioning || this.slides.length <= 1) return

    const totalSlides = this.slides.length
    let index = nextIndex % totalSlides
    if (index < 0) index = totalSlides - 1

    if (index === this.currentIndex) return

    this.isTransitioning = true

    const currentSlide = this.slides[this.currentIndex]
    const nextSlide = this.slides[index]

    // Ensure next slide starts off-screen
    nextSlide.classList.remove("is-active", "is-exiting")
    nextSlide.classList.add("is-entering")

    // Force layout reflow
    nextSlide.offsetHeight

    // Trigger transitions
    currentSlide.classList.remove("is-active")
    currentSlide.classList.add("is-exiting")

    nextSlide.classList.remove("is-entering")
    nextSlide.classList.add("is-active")

    this.currentIndex = index

    // Wait for the transition to finish
    setTimeout(() => {
      currentSlide.classList.remove("is-exiting")
      currentSlide.classList.add("is-entering")
      this.isTransitioning = false
    }, this.animationSpeed * 1000)
  }

  handleBlockSelect(event) {
    this.stopAutoplay()
    const target = event.target
    const blockIndex = this.slides.findIndex(slide => slide === target || slide.contains(target))
    if (blockIndex !== -1 && blockIndex !== this.currentIndex) {
      this.goTo(blockIndex)
    }
  }

  handleBlockDeselect() {
    this.startAutoplay()
  }

  disconnectedCallback() {
    this.stopAutoplay()
    this.removeEventListener("shopify:block:select", this._onBlockSelect)
    this.removeEventListener("shopify:block:deselect", this._onBlockDeselect)
    if (this._onMouseEnter) this.removeEventListener("mouseenter", this._onMouseEnter)
    if (this._onMouseLeave) this.removeEventListener("mouseleave", this._onMouseLeave)
  }
}

if (!customElements.get("red-promo-bar")) {
  customElements.define("red-promo-bar", RedPromoBar)
}
